import { combineReducers } from 'redux'
import request from 'utils/request'
import { capitalize } from 'utils/misc'
import STRINGS from 'utils/strings'
import { _getDefaultMetadata } from '../../../../../utils/PropertyFields'
import {
  enrichTaskMetadataForSave,
  flattenTaskMetadata,
  TASK_METADATA_ENVELOPE_KEYS,
} from '../../../../../utils/taskInstrumentMetadata'
import { ensureCohortAudience, prepareScheduleForPayload } from '../../../modules/Instrument'

//
// Actions
//

const SET_TASKS = 'SET_TASKS'
const VIEW_TASK = 'VIEW_TASK'
const INITIALIZE_PAGE = 'INITIALIZE_PAGE'
const SET_INSTRUMENT = 'SET_INSTRUMENT'

//
// Action Creators
//

const selectTask = task => {
  return {
    type: SET_INSTRUMENT,
    payload: _getTaskInfo(task),
  }
}

const selectTaskToView = task => {
  return {
    type: VIEW_TASK,
    task,
  }
}

const setTasks = tasks => {
  return {
    type: SET_TASKS,
    tasks,
  }
}

const initializePage = () => {
  return {
    type: INITIALIZE_PAGE,
  }
}

const _getTaskInfo = task => {
  if (!task?.task_name) {
    return {
      title: '',
      type: 'TASK',
      metadata: _getDefaultMetadata('TASK'),
    }
  }

  const { display_name, est_duration, task_name, title, required_permissions } = task
  const defaultMetadata = _getDefaultMetadata('TASK')
  const metadata = {
    ...defaultMetadata,
    display_name: display_name || title,
    est_duration,
    task_name,
  }
  if (required_permissions) metadata.required_permissions = required_permissions

  return {
    title,
    type: 'TASK',
    task_name,
    display_name: display_name || title,
    est_duration,
    schedule: defaultMetadata.schedule,
    metadata,
  }
}

/**
 * Build a V2 create payload without nested metadata.metadata.
 * Backend requires task_name, est_duration, display_name, and schedule on the POST root.
 */
export const buildTaskCreatePayload = (task = {}) => {
  const flattened = flattenTaskMetadata(task.metadata)
  const scheduleSource = flattened.schedule || task.schedule
  const preparedSchedule = scheduleSource ? prepareScheduleForPayload(scheduleSource) : undefined

  const taskName = task.task_name || flattened.task_name
  const displayName = task.display_name || flattened.display_name || task.title
  const estDuration = task.est_duration || flattened.est_duration

  let metadata = {
    ...flattened,
    ...(taskName ? { task_name: taskName } : {}),
    ...(displayName ? { display_name: displayName } : {}),
    ...(estDuration ? { est_duration: estDuration } : {}),
    ...(preparedSchedule ? { schedule: preparedSchedule } : {}),
  }

  delete metadata.metadata
  TASK_METADATA_ENVELOPE_KEYS.forEach(key => delete metadata[key])
  metadata = enrichTaskMetadataForSave(metadata, { id: task.id, title: task.title, task_name: taskName })

  const payload = {
    title: task.title,
    type: 'TASK',
    metadata,
  }
  if (taskName) payload.task_name = taskName
  if (displayName) payload.display_name = displayName
  if (estDuration) payload.est_duration = estDuration
  if (preparedSchedule) payload.schedule = preparedSchedule
  return payload
}

export const saveTaskToDB = (studyID, task) => {
  return dispatch => {
    const payload = buildTaskCreatePayload(task)
    ensureCohortAudience(payload)
    return dispatch(
      request({
        method: 'POST',
        url: `/control/v2/studies/${studyID}/instruments`,
        body: JSON.stringify(payload),
        errorMessage: STRINGS.taskSaveErr,
        hasLoader: true,
        success: json => json,
      }),
    )
  }
}

export const fetchTasks = selectedTask => {
  return dispatch => {
    const success = json => {
      dispatch(setTasks(parseTaskList(json.tasks)))
      if (selectedTask) {
        dispatch(selectTaskToView(selectedTask))
      }
      return Promise.resolve(json.tasks)
    }
    return dispatch(
      request({
        method: 'GET',
        url: '/control/task_list',
        errorMessage: 'could not fetch task list',
        success,
        hasLoader: true,
      }),
    )
  }
}

const taskList = (state = [], action) => {
  switch (action.type) {
    case SET_TASKS:
      return action.tasks
    default:
      return state
  }
}

export const reducers = {
  taskList,
}

//
// UTILS
//

const DURATION_SORT_VALUES = {
  short: 0,
  medium: 1,
  long: 2,
}

const placeholderImage = require('../assets/placeholder.png')

export const parseTaskList = rawTasks => {
  return rawTasks.map(task => {
    const { description, display_name, estimated_duration, image, is_hidden, task_id, task_name, title, type } = task
    const taskType = type || 'task'
    const taskTypeVal = capitalize(taskType)
    return [
      {
        key: 'task_name',
        value: title,
        sortValue: title,
        image: image || placeholderImage,
        taskId: task_id,
        taskName: task_name,
        isHidden: !!is_hidden,
        displayName: display_name || title,
      },
      {
        key: 'description',
        value: description,
        sortValue: '',
      },
      { key: 'type', value: taskTypeVal, sortValue: taskTypeVal },
      {
        key: 'estimated_duration',
        value: task.estimated_duration,
        sortValue: DURATION_SORT_VALUES[estimated_duration.toLowerCase()] || -1,
      },
      {
        key: 'actions',
        value: '',
        sortValue: '',
        taskName: task_name,
      },
    ]
  })
}

export default combineReducers(reducers)

export const actions = {
  fetchTasks,
  selectTask,
  selectTaskToView,
  initializePage,
  setTasks,
}
